import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Tooltip,
  Container,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  TextareaAutosize,
  Snackbar
} from '@mui/material';
import {
  Star,
  StarBorder,
  Schedule,
  Place,
  Person,
  Phone,
  Email,
  LocationOn,
  CalendarMonth,
  CalendarToday,
  Tools,
  Check,
  Close,
  AccessTime,
  EventAvailable,
  Message,
  Send,
  CheckCircle,
  Verified,
  Edit,
  Work,
  Description,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  addDoc, 
  serverTimestamp, 
  updateDoc, 
  increment,
  orderBy,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const ProviderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State for provider data
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openBooking, setOpenBooking] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [bookingForm, setBookingForm] = useState({
    serviceType: '',
    date: '',
    time: '',
    period: 'AM',
    description: '',
    duration: '1',
  });
  const [reviews, setReviews] = useState([]);
  const [availability, setAvailability] = useState({
    workingHours: {
      start: '09:00',
      end: '18:00',
    },
    workingDays: [] // Start with empty array until we load from provider
  });
  const [openReview, setOpenReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: '',
  });
  const [availableDays, setAvailableDays] = useState([]);
  const [openMessage, setOpenMessage] = useState(false);
  const [messageForm, setMessageForm] = useState({ message: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(false);
  const [messageError, setMessageError] = useState('');

  useEffect(() => {
    fetchProviderData();
    fetchReviews();
  }, [id]);

  useEffect(() => {
    console.log('Active tab:', activeTab);
    console.log('Reviews state:', reviews);
  }, [activeTab, reviews]);

  useEffect(() => {
    const workingDays = provider?.availability?.workingDays || availability.workingDays;
    console.log("Updating available days from working days:", workingDays);
    const indices = getAvailableDays(workingDays);
    setAvailableDays(indices);
  }, [provider, availability.workingDays]);

  // Use this useEffect to update availableDays whenever workingDays changes
  useEffect(() => {
    console.log('Setting available days based on working days...');
    const workingDays = getWorkingDaysForCalc();
    console.log('Working days for calculation:', workingDays);
    
    if (workingDays.length > 0) {
      const indices = getAvailableDays(workingDays);
      console.log('Setting available day indices:', indices);
      setAvailableDays(indices);
    } else {
      console.warn('No working days available to calculate available days');
      // Default to weekdays if no working days are set
      setAvailableDays([1, 2, 3, 4, 5]);  // Monday to Friday
    }
  }, [provider, availability.workingDays]);

  const fetchProviderData = async () => {
    try {
      const providerDoc = await getDoc(doc(db, 'providers', id));
      if (providerDoc.exists()) {
        const providerData = { id: providerDoc.id, ...providerDoc.data() };
        console.log('Loaded provider data:', providerData);
        console.log('Debug: Raw availability from database:', providerData.availability);
        console.log('Debug: Availability structure keys:', providerData.availability ? Object.keys(providerData.availability) : 'No availability');
        console.log('Debug: Availability has workingDays?', providerData.availability?.workingDays ? 'Yes' : 'No');
        
        // Handle missing services
        if (!providerData.services || !Array.isArray(providerData.services) || providerData.services.length === 0) {
          console.log('No services array, creating default services');
          // Create default services based on category
          providerData.services = generateDefaultServices(providerData.category);
        } else if (typeof providerData.services[0] === 'string') {
          // Convert simple service strings to objects with prices
          providerData.services = providerData.services.map(serviceName => ({
            name: serviceName,
            price: Math.floor(Math.random() * 300) + 200, // Random price between 200-500
            description: `Servicio de ${serviceName}`
          }));
        }
        
        // Handle availability data
        if (!providerData.availability) {
          console.log('No availability data, creating default structure');
          // Create default availability
          providerData.availability = {
            workingDays: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
            workingHours: { start: '09:00', end: '18:00' }
          };
        } 
        // Convert availability format if needed (from boolean to array)
        else if (providerData.availability && !providerData.availability.workingDays) {
          providerData.availability = convertAvailabilityFormat(providerData.availability, providerData);
          console.log('Converted availability format:', providerData.availability);
        }
        
        setProvider(providerData);
        
        // Update availability from provider data if it exists
        if (providerData.availability) {
          console.log('Setting availability from provider data:', providerData.availability);
          console.log('Working days from provider:', providerData.availability.workingDays);
          setAvailability(providerData.availability);
        } else {
          console.log('Provider has no availability data, using default');
        }
      } else {
        setError('Provider not found');
      }
    } catch (error) {
      console.error('Error fetching provider:', error);
      setError('Error loading provider data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      console.log('Starting to fetch reviews for provider:', id);
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('providerId', '==', id),
        orderBy('createdAt', 'desc')
      );
      console.log('Reviews query created:', reviewsQuery);
      const querySnapshot = await getDocs(reviewsQuery);
      console.log('Reviews query snapshot size:', querySnapshot.size);
      const reviewsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Review data:', data);
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date()
        };
      });
      console.log('Processed reviews data:', reviewsData);
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // If the error is about missing index, try fetching without ordering
      if (error.message.includes('requires an index')) {
        try {
          console.log('Falling back to unordered reviews query');
          const fallbackQuery = query(
            collection(db, 'reviews'),
            where('providerId', '==', id)
          );
          const fallbackSnapshot = await getDocs(fallbackQuery);
          const fallbackData = fallbackSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date()
            };
          });
          // Sort the reviews client-side
          fallbackData.sort((a, b) => b.createdAt - a.createdAt);
          setReviews(fallbackData);
        } catch (fallbackError) {
          console.error('Error in fallback reviews query:', fallbackError);
          setError('Error loading reviews. Please try again later.');
        }
      } else {
        setError('Error loading reviews. Please try again later.');
      }
    }
  };

  const handleBookingSubmit = async () => {
    try {
      // Use authenticated user or redirect to login
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      // Create the booking in Firestore
      const bookingRef = await addDoc(collection(db, 'bookings'), {
        ...bookingForm,
        providerId: provider.uid || id,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Usuario',
        phone: currentUser.phone || '',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      
      // Check if conversation exists
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('userId', '==', currentUser.uid),
        where('providerId', '==', provider.uid || id)
      );
      
      const conversationSnapshot = await getDocs(conversationsQuery);
      let conversationId;
      
      // Get or create a conversation
      if (conversationSnapshot.empty) {
        // Create a new conversation
        const newConversation = {
          userId: currentUser.uid,
          providerId: provider.uid || id,
          userName: currentUser.displayName || 'Usuario',
          providerName: provider.name || 'Proveedor',
          lastMessage: `Solicitud de reserva para ${bookingForm.serviceType}`,
          lastMessageDate: serverTimestamp(),
          userUnreadCount: 0,
          providerUnreadCount: 1,
          userPhotoURL: currentUser.photoURL || '',
          providerPhotoURL: provider.photoURL || '',
        };
        
        const conversationRef = await addDoc(collection(db, 'conversations'), newConversation);
        conversationId = conversationRef.id;
      } else {
        // Use existing conversation
        conversationId = conversationSnapshot.docs[0].id;
        // Update conversation with last message
        await updateDoc(doc(db, 'conversations', conversationId), {
          lastMessage: `Solicitud de reserva para ${bookingForm.serviceType}`,
          lastMessageDate: serverTimestamp(),
          providerUnreadCount: increment(1)
        });
      }
      
      // Construct the booking request message
      const messageContent = `He solicitado una reserva para ${bookingForm.serviceType} el día ${bookingForm.date} a las ${bookingForm.time} ${bookingForm.period}. ${bookingForm.description ? `Detalles adicionales: ${bookingForm.description}` : ''}`;
      
      // Create a message document
      await addDoc(collection(db, 'messages'), {
        conversationId,
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Usuario',
        content: messageContent,
        timestamp: serverTimestamp(),
        type: 'booking-request',
        bookingId: bookingRef.id
      });
      
      // Create a notification for the provider
      try {
        await addDoc(collection(db, 'notifications'), {
          userId: provider.uid || id,
          senderId: currentUser.uid,
          senderName: currentUser.displayName || 'Usuario',
          title: 'Nueva solicitud de reserva',
          body: messageContent,
          status: 'unread',
          type: 'booking-request',
          bookingId: bookingRef.id,
          timestamp: serverTimestamp()
        });
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
        // Continue since this is not critical
      }
      
      // Close dialog, reset form and navigate to bookings
      setOpenBooking(false);
      setBookingForm({
        serviceType: '',
        date: '',
        time: '',
        period: 'AM',
        description: '',
        duration: '1',
      });
      navigate('/bookings');
      
    } catch (error) {
      console.error('Error submitting booking:', error);
    }
  };

  const handleReviewSubmit = async () => {
    try {
      // Require authentication
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      // Add the review
      await addDoc(collection(db, 'reviews'), {
        ...reviewForm,
        providerId: id,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || 'Usuario',
        userPhotoURL: currentUser.photoURL || '',
        createdAt: new Date(),
      });
      
      // Update provider's reviewCount and rating
      const providerRef = doc(db, 'providers', id);
      const providerDoc = await getDoc(providerRef);
      if (providerDoc.exists()) {
        const currentReviews = await getDocs(
          query(collection(db, 'reviews'), where('providerId', '==', id))
        );
        const reviewCount = currentReviews.size;
        const totalRating = currentReviews.docs.reduce((acc, doc) => acc + doc.data().rating, 0);
        const newRating = totalRating / reviewCount;
        
        await updateDoc(providerRef, {
          reviewCount: reviewCount,
          rating: newRating,
          updatedAt: new Date()
        });
      }
      
      // Refresh reviews and provider data
      fetchReviews();
      fetchProviderData();
      setOpenReview(false);
      setReviewForm({ rating: 5, comment: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      setError('Error submitting review');
    }
  };

  // Add helper functions to constrain date and time based on provider availability
  const getAvailableDays = (workingDays) => {
    // Map of day names to their index in the week (0-6, where 0 is Sunday)
    const dayMap = {
      'Domingo': 0,
      'Lunes': 1,
      'Martes': 2,
      'Miércoles': 3,
      'Jueves': 4,
      'Viernes': 5,
      'Sábado': 6
    };
    
    // Convert the working days to their indices
    const indices = workingDays.map(day => {
      const index = dayMap[day];
      console.log(`Mapping day: ${day} to index: ${index}`);
      return index;
    });
    
    console.log('Working days being mapped:', workingDays);
    console.log('Available day indices:', indices);
    
    return indices;
  };

  // Use this useEffect to set availability once provider data is loaded
  useEffect(() => {
    if (provider && provider.availability) {
      console.log('Provider availability from DB:', provider.availability);
      
      // Ensure we handle possibly missing or empty working days array
      const workingDays = provider.availability.workingDays || [];
      console.log('Actual working days from provider:', workingDays);
      
      // Set the complete availability object based on what's in the provider data
      setAvailability({
        workingHours: provider.availability.workingHours || {
          start: '09:00',
          end: '18:00',
        },
        workingDays: workingDays
      });
    }
  }, [provider]);

  // Update the isDateAvailable function to directly use provider data
  const isDateAvailable = (dateString) => {
    if (!dateString) return false;
    
    const date = new Date(dateString);
    console.log(`Raw date string: ${dateString}`);
    console.log(`Parsed date object: ${date}`);
    console.log(`Date toString: ${date.toString()}`);
    console.log(`Date toISOString: ${date.toISOString()}`);
    
    // Fix timezone issues by setting to noon
    date.setHours(12, 0, 0, 0);
    
    // IMPORTANT: JavaScript Date uses local timezone for getDay() calls
    // This can cause issues if the date string is parsed differently than expected
    // especially with format YYYY-MM-DD which can be interpreted differently based on timezone
    
    // Use a more reliable method to get the day of week
    // Create a stable UTC date to avoid timezone issues
    const parts = dateString.split('-');
    if (parts.length !== 3) {
      console.error('Invalid date format for validation:', dateString);
      return false;
    }
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
    const day = parseInt(parts[2], 10);
    
    // Create a date explicitly setting year, month, day in local timezone
    const localDate = new Date(year, month, day, 12, 0, 0, 0);
    const dayOfWeek = localDate.getDay(); // 0-6
    
    console.log(`Raw date components: year=${year}, month=${month}, day=${day}`);
    console.log(`Local Date created: ${localDate.toString()}`);
    console.log(`Day of week from getDay(): ${dayOfWeek}`);
    
    const dayName = formatSpanishDay(dayOfWeek);
    
    // Get the working days directly from provider data if available
    const workingDays = provider?.availability?.workingDays || availability.workingDays;
    
    // Check directly against the working days
    const isAvailable = workingDays.includes(dayName);
    
    console.log(`Checking date: ${dateString}, day of week: ${dayOfWeek} (${dayName})`);
    console.log(`Working days from provider:`, workingDays);
    console.log(`Is date available: ${isAvailable}`);
    
    return isAvailable;
  };

  const formatSpanishDay = (dayIndex) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[dayIndex];
  };

  // Get min and max date for booking (today to 3 months in future)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  // Get date boundaries
  const minDate = getMinDate();
  const maxDate = getMaxDate();

  // In the component, get available days from the most up-to-date source
  const getWorkingDaysForCalc = () => {
    return provider?.availability?.workingDays || availability.workingDays || [];
  };

  // Remove the local declaration since we now use state
  // const availableDays = getAvailableDays(getWorkingDaysForCalc());

  // Add this line to debug working days
  useEffect(() => {
    console.log('Working days:', availability.workingDays);
    console.log('Available day indices:', availableDays);
  }, [availability.workingDays, availableDays]);

  // Add this useEffect to validate date changes
  useEffect(() => {
    if (!bookingForm.date) return;
    
    console.log(`useEffect validating date: ${bookingForm.date}`);
    
    // Parse the date using the more reliable method
    const parts = bookingForm.date.split('-');
    if (parts.length !== 3) {
      console.error('Invalid date format in useEffect:', bookingForm.date);
      return;
    }
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
    const day = parseInt(parts[2], 10);
    
    // Create a date explicitly setting year, month, day
    const date = new Date(year, month, day, 12, 0, 0, 0);
    const dayOfWeek = date.getDay();
    const dayName = formatSpanishDay(dayOfWeek);
    
    console.log(`useEffect parsed date: ${date.toString()}`);
    console.log(`useEffect day of week: ${dayOfWeek} (${dayName})`);
    
    // Get working days directly from provider
    const workingDays = provider?.availability?.workingDays || availability.workingDays;
    
    if (!workingDays.includes(dayName)) {
      console.error(`Invalid date selected: ${bookingForm.date} is ${dayName}, not in: ${workingDays.join(', ')}`);
      // Reset the invalid date
      setBookingForm(prev => ({ ...prev, date: '' }));
      alert(`Lo sentimos, el proveedor no trabaja los ${dayName}. Por favor, selecciona otro día.`);
    }
  }, [bookingForm.date]);
  
  // Override the date picker's change handler with a more restrictive version
  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    
    if (!selectedDate) {
      setBookingForm(prev => ({ ...prev, date: '' }));
      return;
    }
    
    console.log(`Date input change: ${selectedDate}`);
    
    // Parse the date using the more reliable method
    const parts = selectedDate.split('-');
    if (parts.length !== 3) {
      console.error('Invalid date format in handler:', selectedDate);
      return;
    }
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
    const day = parseInt(parts[2], 10);
    
    // Create a date explicitly setting year, month, day
    const date = new Date(year, month, day, 12, 0, 0, 0);
    const dayOfWeek = date.getDay();
    const dayName = formatSpanishDay(dayOfWeek);
    
    console.log(`Date selected: ${selectedDate}`);
    console.log(`Date parsed: ${date.toString()}`);
    console.log(`Day of week: ${dayOfWeek} (${dayName})`);
    console.log(`Provider working days from state:`, availability.workingDays);
    
    // Get the actual working days directly from provider data
    const providerWorkingDays = provider?.availability?.workingDays || availability.workingDays;
    console.log('Provider working days from provider data:', providerWorkingDays);
    
    // Strictly check if this day is a working day using the provider's actual data
    if (!providerWorkingDays.includes(dayName)) {
      console.error(`Day ${dayName} is not in provider's working days: ${providerWorkingDays.join(', ')}`);
      // Force a rejection of this date
      alert(`Lo sentimos, el proveedor no trabaja los ${dayName}. Por favor, selecciona otro día.`);
      e.target.value = bookingForm.date || '';
      return;
    }
    
    // If we get here, the day is available
    setBookingForm(prev => ({ ...prev, date: selectedDate }));
  };

  // Direct DOM injection to prevent weekend selection
  useEffect(() => {
    const disableDates = () => {
      const dateInputs = document.querySelectorAll('input[type="date"]');
      
      dateInputs.forEach(input => {
        input.addEventListener('click', (e) => {
          console.log('Date input clicked');
          
          // Use the input event to catch all changes
          input.addEventListener('input', function validateInput(e) {
            if (!this.value) return;
            
            console.log(`Input event on date field: ${this.value}`);
            
            // Use the more reliable date parsing method
            const parts = this.value.split('-');
            if (parts.length !== 3) {
              console.error('Invalid date format in DOM event:', this.value);
              return;
            }
            
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
            const day = parseInt(parts[2], 10);
            
            // Create a date explicitly setting year, month, day
            const selectedDate = new Date(year, month, day, 12, 0, 0, 0);
            const dayOfWeek = selectedDate.getDay();
            const dayName = formatSpanishDay(dayOfWeek);
            
            console.log(`Input parsed date: ${selectedDate.toString()}`);
            console.log(`Input date day of week: ${dayOfWeek} (${dayName})`);
            
            // Get working days directly from provider
            const workingDays = provider?.availability?.workingDays || availability.workingDays;
            
            console.log(`Input event - Selected: ${this.value}, day: ${dayOfWeek} (${dayName})`);
            console.log(`Provider working days:`, workingDays);
            
            // Check directly against provider's working days
            if (!workingDays.includes(dayName)) {
              console.log(`Blocked selection of ${dayName} - not in working days`);
              this.value = bookingForm.date || '';
              e.preventDefault();
              e.stopPropagation();
              
              // Allow this message to show only if no date is currently selected
              if (!bookingForm.date) {
                alert(`Lo sentimos, el proveedor no trabaja los ${dayName}.`);
              }
            }
          });
        }, { once: true });
      });
    };
    
    // Run when dialog opens
    if (openBooking) {
      setTimeout(disableDates, 100);
    }
    
  }, [openBooking, bookingForm.date, provider]);

  // Function to disable specific dates in the calendar UI
  const isDateDisabled = (dateString) => {
    const date = new Date(dateString);
    return !availableDays.includes(date.getDay());
  };

  // Return a color depending on whether the day is a working day
  const getDayStatusColor = (date) => {
    if (!date) return '';
    const dayOfWeek = new Date(date).getDay();
    return availableDays.includes(dayOfWeek) ? '#e6f7e6' : '#ffebeb';
  };

  // Add helper functions for time selection
  const generateTimeSlots = () => {
    const { start, end } = availability.workingHours;
    
    // Convert start and end times to 24-hour format numbers
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);
    
    const startTimeValue = startHours * 60 + startMinutes;
    const endTimeValue = endHours * 60 + endMinutes;
    
    // Generate time slots at 30-minute intervals
    const slots = [];
    for (let time = startTimeValue; time <= endTimeValue; time += 30) {
      const hours = Math.floor(time / 60);
      const minutes = time % 60;
      
      // Convert to 12-hour format
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      
      slots.push({
        value: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
        display: `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
      });
    }
    
    return slots;
  };

  // Function to format the display date
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  };

  // Modified function to convert from time picker value to 24-hour format
  const handleTimeSelection = (value) => {
    setBookingForm({ ...bookingForm, time: value });
  };

  // Add this function to create a proper date string for the shouldDisableDate comparison
  const formatDateForComparison = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Add this function to generate selectable dates
  const generateSelectableDates = () => {
    const dates = [];
    const startDate = new Date(minDate);
    const endDate = new Date(maxDate);
    
    // Set hours to noon to avoid timezone issues
    startDate.setHours(12, 0, 0, 0);
    endDate.setHours(12, 0, 0, 0);
    
    // Generate dates from today to max date
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      
      // Only include dates on available days
      if (availableDays.includes(dayOfWeek)) {
        const dateString = currentDate.toISOString().split('T')[0];
        const displayDate = formatDisplayDate(dateString);
        
        dates.push({
          value: dateString,
          display: displayDate
        });
      }
      
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Convert the boolean availability format to workingDays array
  const convertAvailabilityFormat = useCallback((oldAvailability, providerData = null) => {
    // Log the entire provider data to debug
    console.log('Converting availability format. Provider data available:', !!providerData);
    console.log('Original availability structure:', oldAvailability);
    
    // Check if oldAvailability already has workingDays
    if (oldAvailability.workingDays && Array.isArray(oldAvailability.workingDays) && oldAvailability.workingDays.length > 0) {
      console.log('Availability already has workingDays array:', oldAvailability.workingDays);
      return oldAvailability; // Already in the correct format
    }
    
    const dayMapping = {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo'
    };
    
    // Create an array of working days based on which days are true
    const workingDays = [];
    
    // Method 1: Check boolean properties
    if (typeof oldAvailability.monday === 'boolean') {
      console.log('Using boolean property method for availability conversion');
      if (oldAvailability.monday) workingDays.push('Lunes');
      if (oldAvailability.tuesday) workingDays.push('Martes');
      if (oldAvailability.wednesday) workingDays.push('Miércoles');
      if (oldAvailability.thursday) workingDays.push('Jueves');
      if (oldAvailability.friday) workingDays.push('Viernes');
      if (oldAvailability.saturday) workingDays.push('Sábado');
      if (oldAvailability.sunday) workingDays.push('Domingo');
    } 
    // Method 2: Try to use Object.entries
    else {
      console.log('Using Object.entries method for availability conversion');
      try {
        const availableEntries = Object.entries(oldAvailability)
          .filter(([day, isAvailable]) => isAvailable === true && day in dayMapping)
          .map(([day]) => dayMapping[day]);
        
        if (availableEntries.length > 0) {
          workingDays.push(...availableEntries);
        }
      } catch (error) {
        console.error('Error converting availability with Object.entries:', error);
      }
    }
    
    // If workingDays is still empty, provide default weekday availability
    if (workingDays.length === 0) {
      console.log('Could not determine working days, using default Mon-Fri');
      workingDays.push('Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes');
    }
    
    console.log('Final converted working days:', workingDays);
    
    return {
      workingDays,
      workingHours: oldAvailability.workingHours || {
        start: '09:00',
        end: '18:00'
      }
    };
  }, []);
  
  // Function to generate default services based on provider category
  const generateDefaultServices = (category) => {
    let defaultServices = [];
    
    switch(category) {
      case 'Electricistas':
        defaultServices = [
          { name: 'Instalación eléctrica', price: 350, description: 'Servicio completo de instalación eléctrica residencial o comercial.' },
          { name: 'Reparación de cortocircuitos', price: 250, description: 'Identificación y reparación de problemas en el sistema eléctrico.' },
          { name: 'Mantenimiento preventivo', price: 300, description: 'Revisión del sistema eléctrico para prevenir futuros problemas.' }
        ];
        break;
      case 'Plomeros':
        defaultServices = [
          { name: 'Reparación de fugas', price: 280, description: 'Identificación y reparación de fugas en tuberías y grifos.' },
          { name: 'Instalación de calentadores', price: 450, description: 'Instalación de calentadores de agua de cualquier tipo.' },
          { name: 'Desazolve', price: 350, description: 'Limpieza de drenajes obstruidos.' }
        ];
        break;
      case 'Pintores':
        defaultServices = [
          { name: 'Pintura interior', price: 300, description: 'Servicio de pintura para interiores con acabado profesional.' },
          { name: 'Pintura exterior', price: 380, description: 'Pintura de exteriores con productos de alta durabilidad.' },
          { name: 'Acabados decorativos', price: 420, description: 'Técnicas especiales de acabado: esponjeado, marmoleado, etc.' }
        ];
        break;
      case 'Carpinteros':
        defaultServices = [
          { name: 'Muebles a medida', price: 500, description: 'Diseño y fabricación de muebles personalizados.' },
          { name: 'Restauración', price: 350, description: 'Restauración de muebles antiguos o dañados.' },
          { name: 'Instalación de pisos', price: 450, description: 'Instalación profesional de pisos de madera o laminados.' }
        ];
        break;
      case 'Jardineros':
        defaultServices = [
          { name: 'Diseño de jardines', price: 400, description: 'Diseño personalizado de espacios verdes.' },
          { name: 'Poda', price: 250, description: 'Poda de árboles, arbustos y plantas ornamentales.' },
          { name: 'Sistema de riego', price: 350, description: 'Instalación de sistemas de riego automático.' }
        ];
        break;
      default:
        defaultServices = [
          { name: 'Servicio básico', price: 250, description: 'Servicio estándar por hora.' },
          { name: 'Servicio premium', price: 400, description: 'Servicio completo con garantía extendida.' },
          { name: 'Consultoría', price: 300, description: 'Asesoría profesional personalizada.' }
        ];
    }
    
    console.log('Generated default services for category:', category, defaultServices);
    return defaultServices;
  };
  
  // Function to double-check provider data from database
  const checkProviderData = useCallback(async () => {
    try {
      if (provider?.id) {
        console.log("Double-checking provider data for ID:", provider.id);
        const providerDoc = await getDoc(doc(db, 'providers', provider.id));
        if (providerDoc.exists()) {
          const rawData = providerDoc.data();
          console.log("Raw provider data from database:", rawData);
          console.log("Raw availability from database:", rawData.availability);
          
          // Check if we need to update local provider data
          if (rawData.availability && (!provider.availability?.workingDays || provider.availability.workingDays.length === 0)) {
            console.log("Provider data needs availability conversion from database");
            const convertedAvailability = convertAvailabilityFormat(rawData.availability, rawData);
            console.log("Converted availability from raw data:", convertedAvailability);
            
            // Update provider with the correctly converted availability
            setProvider(prev => ({
              ...prev,
              availability: convertedAvailability
            }));
            
            // Also update the availability state
            setAvailability(convertedAvailability);
          }
        }
      }
    } catch (error) {
      console.error("Error double-checking provider data:", error);
    }
  }, [provider, convertAvailabilityFormat, setProvider, setAvailability]);
  
  // Call the check function when component mounts
  useEffect(() => {
    if (!error && provider) {
      checkProviderData();
    }
  }, [error, provider, checkProviderData]);

  // Add function to handle message send
  const handleSendMessage = async () => {
    // Don't send empty messages
    if (!messageForm.message.trim()) return;
    
    // Require authentication
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    setSendingMessage(true);
    setMessageError('');
    
    try {      
      // Make sure we have a valid provider ID
      const providerId = provider?.uid || id;
      if (!providerId) {
        setMessageError('Error: No se pudo identificar al proveedor');
        setSendingMessage(false);
        return;
      }
      
      // Check if a conversation already exists
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('userId', '==', currentUser.uid),
        where('providerId', '==', providerId)
      );
      
      const conversationSnapshot = await getDocs(conversationsQuery);
      let conversationId;
      let isNewConversation = false;
      
      // If conversation exists, use it
      if (!conversationSnapshot.empty) {
        conversationId = conversationSnapshot.docs[0].id;
      } else {
        // Create a new conversation
        isNewConversation = true;
        const newConversationData = {
          userId: currentUser.uid,
          providerId: providerId,
          providerName: provider?.name || provider?.displayName || 'Proveedor',
          providerPhotoURL: provider?.photoURL || provider?.image || '',
          userName: currentUser.displayName || currentUser.email || 'Usuario',
          userPhotoURL: currentUser.photoURL || '',
          lastMessage: messageForm.message,
          lastMessageTimestamp: serverTimestamp(),
          userUnreadCount: 0,
          providerUnreadCount: 1,
          createdAt: serverTimestamp()
        };
        
        try {
          const newConversationRef = await addDoc(collection(db, 'conversations'), newConversationData);
          conversationId = newConversationRef.id;
        } catch (error) {
          console.error('Error creating conversation:', error);
          setMessageError('Error al crear la conversación. Intenta nuevamente.');
          setSendingMessage(false);
          return;
        }
      }
      
      // Verify the conversation exists before adding a message
      try {
        const conversationDoc = await getDoc(doc(db, 'conversations', conversationId));
        if (!conversationDoc.exists()) {
          console.error('Conversation does not exist after creation');
          setMessageError('Error al crear la conversación. Intenta nuevamente.');
          setSendingMessage(false);
          return;
        }
        
        // Add the message to the conversation
        await addDoc(collection(db, 'messages'), {
          conversationId: conversationId,
          senderId: currentUser.uid,
          recipientId: providerId,
          content: messageForm.message,
          timestamp: serverTimestamp(),
          isRead: false,
          type: 'chat'
        });
        
        // If an existing conversation, update it with the new message
        if (!isNewConversation) {
          await updateDoc(doc(db, 'conversations', conversationId), {
            lastMessage: messageForm.message,
            lastMessageTimestamp: serverTimestamp(),
            providerUnreadCount: increment(1)
          });
        }
        
        setMessageSuccess(true);
        setMessageForm({ message: '' });
        setOpenMessage(false);
        
        // Redirect to messages page with conversation open
        navigate(`/messages?conversation=${conversationId}`);
      } catch (error) {
        console.error('Error sending message:', error);
        setMessageError('Error al enviar el mensaje. Intenta nuevamente.');
        setSendingMessage(false);
      }
    } catch (error) {
      console.error('Error in message handling:', error);
      setMessageError('Error al enviar el mensaje. Intenta nuevamente.');
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const averageRating = reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length || 0;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box 
        sx={{ 
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          position: 'relative',
          mb: 3,
          mt: { xs: 8, md: 2 },
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0',
          borderRadius: '16px',
          mx: { xs: 2, md: 0 },
          overflow: 'hidden'
        }}
      >
        {/* Yellow accent bar */}
        <Box 
          sx={{ 
            height: '4px', 
            bgcolor: '#FFE500',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0
          }} 
        />

        <Container maxWidth="lg">
          <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Grid container spacing={4} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  position: 'relative', 
                  textAlign: 'center',
                  mb: { xs: 2, md: 0 }
                }}>
                  <Avatar
                    src={provider?.image}
                    alt={provider?.name}
                    sx={{ 
                      width: { xs: 140, md: 160 }, 
                      height: { xs: 140, md: 160 }, 
                      mx: 'auto',
                      border: '4px solid #fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02)'
                      }
                    }}
                  />
                  {provider?.isTopRated && (
                    <Chip
                      icon={<Verified sx={{ color: '#1976d2 !important' }} />}
                      label="Verificado"
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'white',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        '& .MuiChip-label': {
                          color: '#1976d2',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  )}
                  
                  {/* Only show Edit Profile Button if current user is the provider */}
                  {currentUser && currentUser.uid === (provider?.uid || id) && (
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/profile/${id}`)}
                      sx={{ 
                        mt: 2, 
                        borderColor: '#e0e0e0',
                        color: '#666',
                        fontSize: '0.875rem',
                        '&:hover': {
                          borderColor: '#FFE500',
                          bgcolor: 'rgba(255, 229, 0, 0.08)'
                        },
                        textTransform: 'none',
                        fontFamily: 'Ubuntu',
                        fontWeight: 500
                      }}
                      startIcon={<Edit />}
                    >
                      Editar Perfil
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Box sx={{ pl: { md: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    flexWrap: 'wrap', 
                    gap: 1.5,
                    mb: 2 
                  }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        fontFamily: 'Ubuntu',
                        color: '#2C3E50',
                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {provider?.name}
                    </Typography>
                    <Chip 
                      label={provider?.category}
                      size="small"
                      sx={{ 
                        bgcolor: '#FFE500',
                        fontWeight: 500,
                        '& .MuiChip-label': { px: 1.5 },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    />
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 2,
                    flexWrap: 'wrap'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      bgcolor: 'rgba(0,0,0,0.02)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1
                    }}>
                      <Rating value={averageRating} precision={0.5} readOnly size="small" />
                      <Typography variant="body2" color="text.secondary">
                        ({reviews.length} reseñas)
                      </Typography>
                    </Box>
                    <Chip 
                      icon={<LocationOn sx={{ color: '#666 !important', fontSize: '1.1rem' }} />}
                      label={provider?.location}
                      variant="outlined"
                      size="small"
                      sx={{ 
                        borderColor: '#e0e0e0',
                        '& .MuiChip-label': { 
                          fontSize: '0.875rem',
                          color: '#666'
                        }
                      }}
                    />
                  </Box>

                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#666',
                      mb: 3,
                      maxWidth: 800,
                      lineHeight: 1.6,
                      fontSize: '1rem',
                      letterSpacing: '0.01em'
                    }}
                  >
                    {provider?.description}
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mb: 3,
                    flexWrap: 'wrap'
                  }}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: '#FFD05B',
                        color: '#1e1e1e',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#e6bb52',
                        },
                        mb: 2,
                      }}
                      onClick={() => {
                        if (currentUser) {
                          setOpenBooking(true);
                        } else {
                          navigate('/login');
                        }
                      }}
                    >
                      {currentUser ? 'Reservar Servicio' : 'Iniciar Sesión para Reservar'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Message />}
                      onClick={() => currentUser ? setOpenMessage(true) : navigate('/login')}
                      sx={{ 
                        mt: 2,
                        bgcolor: '#FFE500',
                        color: 'black',
                        borderRadius: '8px',
                        '&:hover': { 
                          bgcolor: '#FFD700',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        },
                        fontFamily: 'Ubuntu',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      {currentUser ? 'Enviar Mensaje' : 'Iniciar Sesión para Mensajes'}
                    </Button>
                  </Box>

                  <Box 
                    sx={{ 
                      display: 'flex', 
                      gap: { xs: 2, md: 4 },
                      flexWrap: 'wrap',
                      p: 2.5,
                      bgcolor: 'rgba(0,0,0,0.02)',
                      borderRadius: '12px',
                      border: '1px solid rgba(0,0,0,0.05)'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        Horario
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                        {availability.workingHours.start} - {availability.workingHours.end}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        Tarifa
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                        Desde ${provider?.hourlyRate}/hr
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        Servicios Completados
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                        {provider?.completedServices || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            <Paper 
              sx={{ 
                mb: 4,
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}
            >
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  bgcolor: '#fff',
                  '& .MuiTab-root': {
                    fontFamily: 'Ubuntu',
                    fontWeight: 500,
                    fontSize: '1rem',
                    textTransform: 'none',
                    color: '#666',
                    '&.Mui-selected': {
                      color: '#2C3E50'
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#FFE500',
                    height: '3px'
                  }
                }}
              >
                <Tab label="Servicios" />
                <Tab label="Reseñas" />
                <Tab label="Disponibilidad" />
              </Tabs>

              <Box sx={{ p: 3, bgcolor: '#fff' }}>
                {activeTab === 0 && (
                  <Grid container spacing={3}>
                    {provider?.services?.map((service, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Card 
                          sx={{ 
                            height: '100%',
                            border: '1px solid #e0e0e0',
                            boxShadow: 'none',
                            borderRadius: '12px',
                            '&:hover': {
                              borderColor: '#FFE500',
                              transform: 'translateY(-4px)',
                              transition: 'all 0.2s',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <CardContent sx={{ p: 2.5 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: '#2C3E50', fontWeight: 600 }}>
                              {service.name}
                            </Typography>
                            <Typography variant="h5" gutterBottom sx={{ color: '#2C3E50', fontWeight: 700 }}>
                              ${service.price}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.6 }}>
                              {service.description}
                            </Typography>
                            <Button
                              variant="outlined"
                              fullWidth
                              sx={{ 
                                borderColor: '#FFE500',
                                color: '#2C3E50',
                                borderRadius: '8px',
                                '&:hover': {
                                  borderColor: '#FFD700',
                                  bgcolor: '#FFE500',
                                  transform: 'translateY(-1px)'
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                              onClick={() => {
                                if (currentUser) {
                                  setBookingForm(prev => ({ ...prev, serviceType: service.name }));
                                  setOpenBooking(true);
                                } else {
                                  navigate('/login');
                                }
                              }}
                            >
                              {currentUser ? 'Reservar' : 'Iniciar Sesión'}
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {activeTab === 1 && (
                  <Box>
                    {console.log('Rendering reviews tab')}
                    {console.log('Reviews length:', reviews.length)}
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ color: '#2C3E50', fontWeight: 600 }}>
                        Reseñas ({reviews.length})
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => currentUser ? setOpenReview(true) : navigate('/login')}
                        startIcon={<Star />}
                        sx={{ 
                          borderColor: '#FFE500',
                          color: '#2C3E50',
                          borderRadius: '8px',
                          '&:hover': {
                            borderColor: '#FFD700',
                            bgcolor: '#FFE500',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          fontFamily: 'Ubuntu'
                        }}
                      >
                        {currentUser ? 'Escribir Reseña' : 'Iniciar Sesión para Reseñar'}
                      </Button>
                    </Box>

                    {reviews.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        {console.log('No reviews to display')}
                        <Typography variant="body1" color="text.secondary">
                          No hay reseñas aún. ¡Sé el primero en escribir una!
                        </Typography>
                      </Box>
                    ) : (
                      reviews.map((review) => {
                        console.log('Rendering review:', review);
                        return (
                          <Box 
                            key={review.id} 
                            sx={{ 
                              mb: 3,
                              p: 2.5,
                              borderRadius: '12px',
                              border: '1px solid #e0e0e0',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                              bgcolor: '#fff',
                              '&:hover': {
                                boxShadow: '0 3px 6px rgba(0,0,0,0.08)',
                                borderColor: '#d0d0d0',
                                transition: 'all 0.2s'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar 
                                  src={review.userPhotoURL} 
                                  alt={review.userName || 'Usuario'}
                                  sx={{ 
                                    width: 48, 
                                    height: 48,
                                    bgcolor: review.userPhotoURL ? 'transparent' : '#FFE500',
                                    color: '#222',
                                    fontWeight: 600
                                  }}
                                >
                                  {!review.userPhotoURL && (review.userName ? review.userName.charAt(0).toUpperCase() : 'U')}
                                </Avatar>
                                <Box sx={{ ml: 1.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2C3E50', mb: 0.5 }}>
                                    {review.userName || 'Usuario Anónimo'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Rating 
                                      value={review.rating} 
                                      readOnly 
                                      size="small"
                                      sx={{
                                        '& .MuiRating-iconFilled': {
                                          color: '#FFD700'
                                        }
                                      }} 
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '0.875rem' }}>
                                      {new Date(review.createdAt).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                            <Typography
                              variant="body1"
                              sx={{
                                color: '#444',
                                lineHeight: 1.6,
                                fontSize: '1rem',
                                whiteSpace: 'pre-line'
                              }}
                            >
                              {review.comment}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#2C3E50' }}>
                      Horario de Trabajo
                    </Typography>
                    <List>
                      {availability.workingDays.map((day) => (
                        <ListItem 
                          key={day}
                          sx={{
                            borderBottom: '1px solid #e0e0e0',
                            '&:last-child': {
                              borderBottom: 'none'
                            }
                          }}
                        >
                          <ListItemIcon>
                            <CalendarToday sx={{ color: '#666' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={day}
                            secondary={`${availability.workingHours.start} - ${availability.workingHours.end}`}
                            primaryTypographyProps={{
                              sx: { color: '#2C3E50', fontWeight: 500 }
                            }}
                            secondaryTypographyProps={{
                              sx: { color: '#666' }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                mb: 4, 
                boxShadow: 'none', 
                border: '1px solid #e0e0e0',
                borderRadius: '16px',
                overflow: 'hidden'
              }}
            >
              {/* Yellow accent bar */}
              <Box sx={{ height: '4px', bgcolor: '#FFE500' }} />
              
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#2C3E50', fontWeight: 600, mb: 2 }}>
                  Información de Contacto
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Phone sx={{ color: '#666' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Teléfono" 
                      secondary={currentUser ? provider?.phone : "XXXXXXXXXX"}
                      primaryTypographyProps={{
                        sx: { color: '#666', fontSize: '0.875rem' }
                      }}
                      secondaryTypographyProps={{
                        sx: { color: '#2C3E50', fontWeight: 500 }
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Email sx={{ color: '#666' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Email" 
                      secondary={currentUser ? provider?.email : "XXXXXXXXXX"}
                      primaryTypographyProps={{
                        sx: { color: '#666', fontSize: '0.875rem' }
                      }}
                      secondaryTypographyProps={{
                        sx: { color: '#2C3E50', fontWeight: 500 }
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <LocationOn sx={{ color: '#666' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Ubicación" 
                      secondary={provider?.location}
                      primaryTypographyProps={{
                        sx: { color: '#666', fontSize: '0.875rem' }
                      }}
                      secondaryTypographyProps={{
                        sx: { color: '#2C3E50', fontWeight: 500 }
                      }}
                    />
                  </ListItem>
                </List>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Message />}
                  onClick={() => setOpenMessage(true)}
                  sx={{ 
                    mt: 2,
                    bgcolor: '#FFE500',
                    color: 'black',
                    borderRadius: '8px',
                    '&:hover': { 
                      bgcolor: '#FFD700',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    },
                    fontFamily: 'Ubuntu',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  {currentUser ? 'Enviar Mensaje' : 'Iniciar Sesión para Mensajes'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Booking Dialog */}
      <Dialog 
        open={openBooking} 
        onClose={() => setOpenBooking(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontFamily: 'Ubuntu',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#2C3E50',
            pt: 2.5,
            pb: 2.5,
            px: 3,
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CalendarToday fontSize="small" sx={{ color: '#FFD700' }} />
          Reservar Servicio
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2.5,
            mb: 2,
            mt: 1 
          }}>
            <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem' }}>
              Detalles del Servicio
            </Typography>
            
            <FormControl fullWidth required>
              <InputLabel>Servicio</InputLabel>
              <Select
                value={bookingForm.serviceType}
                label="Servicio"
                onChange={(e) => setBookingForm({ ...bookingForm, serviceType: e.target.value })}
                sx={{ borderRadius: '8px' }}
              >
                {provider?.services?.map((service, index) => (
                  <MenuItem key={index} value={service.name}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body1">{service.name}</Typography>
                      <Typography variant="body1" fontWeight={600}>${service.price}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ bgcolor: '#f9f9f9', p: 2, borderRadius: '8px', border: '1px solid #eee' }}>
              <Typography variant="subtitle2" sx={{ color: '#2C3E50', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime fontSize="small" sx={{ color: '#FFD700' }} />
                Disponibilidad del Proveedor
              </Typography>
              
              <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                Días disponibles: {(provider?.availability?.workingDays || availability.workingDays).join(", ")}
              </Typography>
              
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Horario: {availability.workingHours.start} - {availability.workingHours.end}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500, fontSize: '0.875rem' }}>
                    Fecha
                  </Typography>
                  <TextField
                    type="date"
                    value={bookingForm.date}
                    onChange={handleDateChange}
                    fullWidth
                    required
                    error={bookingForm.date && !isDateAvailable(bookingForm.date)}
                    helperText={bookingForm.date && !isDateAvailable(bookingForm.date) ? "Esta fecha no está disponible" : ""}
                    inputProps={{
                      min: minDate,
                      max: maxDate
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: bookingForm.date ? getDayStatusColor(bookingForm.date) : 'transparent',
                        transition: 'background-color 0.3s ease'
                      }
                    }}
                  />
                  {bookingForm.date && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        color: '#666',
                        gap: 0.5,
                        mt: 1
                      }}
                    >
                      <CalendarToday fontSize="small" sx={{ fontSize: '0.9rem', color: '#FFD700' }} />
                      {formatDisplayDate(bookingForm.date)}
                    </Typography>
                  )}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    mt: 1.5,
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: '6px'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#2C3E50', mb: 0.5 }}>
                      Días laborables:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((day, index) => {
                        // Use provider data directly to determine if this is a working day
                        const workingDays = provider?.availability?.workingDays || availability.workingDays;
                        const isWorkingDay = workingDays.includes(day);
                        
                        return (
                          <Chip
                            key={day}
                            label={day}
                            size="small"
                            sx={{
                              bgcolor: isWorkingDay ? '#e6f7e6' : '#ffebeb',
                              color: isWorkingDay ? '#388e3c' : '#d32f2f',
                              fontWeight: isWorkingDay ? 500 : 400,
                              opacity: isWorkingDay ? 1 : 0.7,
                              fontSize: '0.75rem',
                              height: '24px'
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500, fontSize: '0.875rem' }}>
                    Hora
                  </Typography>
                  <FormControl fullWidth required error={!bookingForm.time}>
                    <Select
                      value={bookingForm.time}
                      onChange={(e) => handleTimeSelection(e.target.value)}
                      displayEmpty
                      sx={{ borderRadius: '8px' }}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 300 }
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <Typography sx={{ color: '#999' }}>Selecciona una hora</Typography>;
                        }
                        
                        // Convert the 24-hour time to display format
                        const [hours, minutes] = selected.split(':').map(Number);
                        const period = hours >= 12 ? 'PM' : 'AM';
                        const hours12 = hours % 12 || 12;
                        return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
                      }}
                    >
                      {generateTimeSlots().map((slot) => (
                        <MenuItem key={slot.value} value={slot.value}>
                          {slot.display}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Selecciona un horario dentro del rango disponible
                    </FormHelperText>
                  </FormControl>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                    Horario: {availability.workingHours.start} - {availability.workingHours.end}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            <FormControl fullWidth>
              <InputLabel>Duración (horas)</InputLabel>
              <Select
                value={bookingForm.duration}
                label="Duración (horas)"
                onChange={(e) => setBookingForm({ ...bookingForm, duration: e.target.value })}
                sx={{ borderRadius: '8px' }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour} {hour === 1 ? 'hora' : 'horas'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 500, fontSize: '0.875rem' }}>
                Descripción del trabajo
              </Typography>
              <TextField
                placeholder="Describe detalladamente el trabajo que necesitas..."
                multiline
                rows={4}
                fullWidth
                value={bookingForm.description}
                onChange={(e) => setBookingForm({ ...bookingForm, description: e.target.value })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '30px',
                    backgroundColor: '#fff',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    height: '120px',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#d0d0d0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '100% !important',
                    padding: '16px 24px',
                    fontSize: '1rem',
                    '&::placeholder': {
                      fontSize: '1rem',
                      opacity: 0.7,
                      color: '#666',
                      position: 'absolute',
                      top: 16,
                      transform: 'none'
                    }
                  },
                  '& .MuiInputBase-multiline': {
                    padding: '0',
                    '& textarea': {
                      height: '100% !important'
                    }
                  }
                }}
                helperText="Incluye medidas, materiales o cualquier detalle específico que el proveedor deba saber"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
          <Button 
            onClick={() => setOpenBooking(false)}
            sx={{ 
              fontFamily: 'Ubuntu',
              color: '#666',
              px: 3
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleBookingSubmit} 
            variant="contained"
            disabled={!bookingForm.serviceType || !bookingForm.date || !bookingForm.time || 
              (bookingForm.date && !isDateAvailable(bookingForm.date))}
            sx={{ 
              bgcolor: '#FFD700',
              color: 'rgba(0,0,0,0.87)',
              fontWeight: 600,
              px: 3,
              borderRadius: '8px',
              '&:hover': { 
                bgcolor: '#FFC700',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              },
              '&.Mui-disabled': {
                bgcolor: '#f5f5f5',
                color: '#bdbdbd'
              },
              transition: 'all 0.2s ease-in-out',
              fontFamily: 'Ubuntu'
            }}
          >
            Reservar Ahora
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog 
        open={openReview} 
        onClose={() => setOpenReview(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxWidth: '450px'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontFamily: 'Ubuntu',
            fontSize: '1.125rem',
            fontWeight: 500,
            color: '#2C3E50',
            pt: 2,
            pb: 1.5,
            px: 2.5,
            borderBottom: '1px solid #eee'
          }}
        >
          Escribir Reseña
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#2C3E50',
                  fontWeight: 400,
                  fontSize: '0.9375rem',
                  mb: 1
                }}
              >
                Calificación:
              </Typography>
              <Rating
                value={reviewForm.rating}
                onChange={(event, newValue) => {
                  setReviewForm({ ...reviewForm, rating: newValue });
                }}
                size="large"
                sx={{
                  '& .MuiRating-icon': {
                    fontSize: '2rem'
                  },
                  '& .MuiRating-iconFilled': {
                    color: '#FFD700'
                  },
                  '& .MuiRating-iconHover': {
                    color: '#FFD700'
                  },
                  '& .MuiRating-iconEmpty': {
                    color: 'rgba(0,0,0,0.15)'
                  }
                }}
              />
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                {reviewForm.rating === 5 && "¡Excelente!"}
                {reviewForm.rating === 4 && "Muy Bueno"}
                {reviewForm.rating === 3 && "Bueno"}
                {reviewForm.rating === 2 && "Regular"}
                {reviewForm.rating === 1 && "Malo"}
              </Typography>
            </Box>

            <Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#2C3E50',
                  fontWeight: 400,
                  fontSize: '0.9375rem',
                  mb: 1
                }}
              >
                Tu reseña
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                placeholder="¿Qué te pareció el servicio? ¿El proveedor fue puntual? ¿El trabajo cumplió con tus expectativas?"
                variant="outlined"
                sx={{
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '30px',
                    backgroundColor: '#fff',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    height: '120px',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#d0d0d0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '100% !important',
                    padding: '16px 24px',
                    fontSize: '1rem',
                    '&::placeholder': {
                      fontSize: '1rem',
                      opacity: 0.7,
                      color: '#666',
                      position: 'absolute',
                      top: 16,
                      transform: 'none'
                    }
                  },
                  '& .MuiInputBase-multiline': {
                    padding: '0',
                    '& textarea': {
                      height: '100% !important'
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: 2.5, 
            py: 2,
            gap: 1,
            borderTop: '1px solid #eee'
          }}
        >
          <Button 
            onClick={() => setOpenReview(false)}
            sx={{ 
              fontFamily: 'Ubuntu',
              color: '#666',
              px: 2,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleReviewSubmit}
            variant="contained"
            disabled={!reviewForm.comment.trim()}
            sx={{ 
              bgcolor: '#e0e0e0',
              color: '#666',
              fontWeight: 400,
              px: 2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { 
                bgcolor: '#d5d5d5',
                boxShadow: 'none'
              },
              '&:not(:disabled)': {
                bgcolor: '#FFD700',
                color: 'rgba(0,0,0,0.87)',
                '&:hover': {
                  bgcolor: '#FFC700',
                  boxShadow: 'none'
                }
              },
              fontFamily: 'Ubuntu'
            }}
          >
            Enviar Reseña
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Message Dialog */}
      <Dialog 
        open={openMessage} 
        onClose={() => setOpenMessage(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontFamily: 'Ubuntu',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#2C3E50',
            pt: 2.5,
            pb: 2.5,
            px: 3,
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Message fontSize="small" sx={{ color: '#FFD700' }} />
          Enviar Mensaje a {provider?.name || provider?.displayName}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2.5,
            mb: 2,
            mt: 1 
          }}>
            <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem' }}>
              Escribe tu mensaje
            </Typography>

            <Alert severity="info" sx={{ mb: 1 }}>
              Estás enviando este mensaje como "Cliente" (usuario por defecto).
            </Alert>
            
            <Box sx={{ 
              backgroundColor: '#f9f9f9', 
              borderRadius: '12px', 
              border: '1px solid rgba(0, 0, 0, 0.23)',
              '&:hover': {
                border: '1px solid rgba(0, 0, 0, 0.87)'
              },
              position: 'relative'
            }}>
              <textarea
                value={messageForm.message}
                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                placeholder="Escribe tu mensaje aquí..."
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  boxSizing: 'border-box',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  backgroundColor: 'transparent',
                  borderRadius: '12px',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  lineHeight: '1.5',
                  minHeight: '120px',
                  color: '#333'
                }}
              />
            </Box>
            
            {messageError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {messageError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
          <Button 
            onClick={() => setOpenMessage(false)}
            sx={{ 
              color: '#666',
              borderRadius: '8px',
              fontFamily: 'Ubuntu',
              '&:hover': { 
                bgcolor: '#f5f5f5',
              },
            }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained"
            startIcon={sendingMessage ? <CircularProgress size={20} color="inherit" /> : <Send />}
            onClick={handleSendMessage}
            disabled={!messageForm.message.trim() || sendingMessage}
            sx={{ 
              bgcolor: '#FFE500',
              color: 'black',
              borderRadius: '8px',
              '&:hover': { 
                bgcolor: '#FFD700'
              },
              '&:disabled': {
                bgcolor: '#f5f5f5',
                color: '#999'
              },
              fontFamily: 'Ubuntu'
            }}
          >
            {sendingMessage ? 'Enviando...' : 'Enviar Mensaje'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar 
        open={messageSuccess} 
        autoHideDuration={6000} 
        onClose={() => setMessageSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setMessageSuccess(false)} 
          severity="success" 
          sx={{ width: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
        >
          Mensaje enviado con éxito
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderProfile; 